import { useEffect, useRef, useState, useMemo, useCallback, lazy, Suspense } from "react";
import { OwnerEditButton } from "../components/OwnerEditButton";
const ProductEditDialog = lazy(() => import("./admin/ProductEditDialog").then((m) => ({ default: m.ProductEditDialog })));
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API, money, moneyUnit, productImage, productThumb } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ShoppingCart, Bookmark, CheckCircle2, Truck, FileText, Sparkles, Download, HelpCircle, BookOpen } from "lucide-react";
import LiveSizePreview from "../components/LiveSizePreview";
import ProductOptions from "../components/ProductOptions";
import ArtworkUploader from "../components/ArtworkUploader";
import { ProductTools } from "../components/resources/ProductTools";
import { rememberToolSource, toolSource, trackTool } from "../lib/track";
import LargeFormatJobs from "../components/LargeFormatJobs";
import ProductAddons from "../components/ProductAddons";
import TurnaroundPicker from "../components/TurnaroundPicker";
import { FloatingPriceCard, InFlowPriceCard, useInViewport } from "../components/PriceCalculator";
import { LabelPriceLadder } from "../components/LabelPriceLadder";
import { LabelStockGuide } from "../components/LabelStockGuide";
import { generateDieTemplate, generateDieSvg, POPULAR_DIE_SIZES } from "../lib/dieTemplate";
import { getTemplatePresetsForProduct, templateHref } from "../lib/productTemplates";
import { useSeoHead, productSchema, breadcrumbSchema, splitSeoBody } from "../lib/seo";
import { buildProductFaqs, faqJsonLd as faqJsonLdFor } from "../lib/productFaq.mjs";
import { useServiceCategories } from "../lib/categories";
import FaqAccordion from "../components/FaqAccordion";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { useIsMobile } from "../hooks/useIsMobile";
import { MobileProductLayout } from "../components/mobile/MobileProductLayout";
import { MadeInUSABadge } from "../components/MadeInUSABadge";
import BoxProductPage from "./box/BoxProductPage";
import { MoreAboutProduct } from "../components/MoreAboutProduct";
import { SeoProse, cleanDescription } from "../components/SeoProse";
import { FoilCodePicker, FOIL_RECOMMEND } from "../components/FoilCodePicker";
import { ColorplanShadePicker, COLORPLAN_RECOMMEND } from "../components/ColorplanShadePicker";

// Fire-and-forget analytics ping for die-template downloads. Never awaited,
// never surfaces errors — the download itself must always succeed even if
// the analytics endpoint is unreachable.
function logTemplateDownload(payload) {
  try {
    api.post("/marketing/events/template-download", payload).catch(() => {});
  } catch {
    /* noop */
  }
}

const LARGE_FORMAT_CATEGORIES = new Set(["retractables-banners"]);

// Render a modifier (material/adhesive) badge based on its mode.
// Storefront should hide raw +% / + $ amounts (kept internal), and only show
// a clean "×N" multiplier when applicable.
function formatModifier(m) {
  if (!m) return "";
  const mode = m.mode || (m.price_multiplier != null ? "multiplier" : "");
  const val = m.price_value != null ? Number(m.price_value) : Number(m.price_multiplier);
  if (!Number.isFinite(val)) return "";
  if (mode === "multiplier") return `×${val.toFixed(2)}`;
  return "Included";
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Push a width/height (inches) from a calculator into the configurator, clamped to the product's limits.
  const applySizeFromTool = (w, h, p, quiet = false) => {
    const prod = p || product;
    const cd = prod?.custom_dimensions;
    const lim = (a, b) => (Number(a) > 0 ? Number(a) : Number(b) > 0 ? Number(b) : undefined);
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo ?? v), hi ?? v);
    const W = Math.round(clamp(w, lim(cd?.min_width, cd?.min_in) ?? lim(prod?.min_width_in), lim(cd?.max_width, cd?.max_in) ?? lim(prod?.max_width_in)) * 100) / 100;
    const H = Math.round(clamp(h, lim(cd?.min_height, cd?.min_in) ?? lim(prod?.min_height_in), lim(cd?.max_height, cd?.max_in) ?? lim(prod?.max_height_in)) * 100) / 100;
    setConfig((c) => ({ ...c, width_in: String(W), height_in: String(H) }));
    if (quiet) return;
    toast.success(`Size set to ${W}" × ${H}"${W !== w || H !== h ? " (adjusted to this product's limits)" : ""}`);
    document.getElementById("configurator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const { user } = useAuth();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [ownerEditing, setOwnerEditing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const handoffRef = useRef(null);
  const [config, setConfig] = useState({
    quantity: 100, material_key: "", size: "", shape: "", color: "",
    adhesive_key: "", corners: "", height_in: "", width_in: "",
    lamination_key: "none", varnish_key: "none", foil_key: "none",
    pole_pocket_key: "none", hem_key: "none", grommet_key: "none",
    finishing_selections: {},
    core_size: "", labels_per_roll: "", unwind_direction: "", outside_diameter: ""
  });
  const [price, setPrice] = useState(null);
  const [calcing, setCalcing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null); // { file_id, original_filename, source_filename }
  const [jobs, setJobs] = useState([]); // multi-job state for large-format products
  const [productionNotes, setProductionNotes] = useState("");
  const [foilCode, setFoilCode] = useState(() => new URLSearchParams(window.location.search).get("foilCode") || "");
  const [colorplanShade, setColorplanShade] = useState(() => new URLSearchParams(window.location.search).get("colorplan") || "");
  const [selectedAddons, setSelectedAddons] = useState([]); // string[] of addon keys
  const [turnaroundKey, setTurnaroundKey] = useState(""); // selected turnaround option key
  const [saving, setSaving] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [inFlowPriceRef, isPriceInView] = useInViewport([product]);
  const isMobile = useIsMobile();
  // Live shipping presets (box_size → per-method $). Refreshed once per page
  // load; the widget below resolves preset tiers against this doc so admin
  // changes to /admin/shipping-presets ripple across every PDP without needing
  // to re-save the product.
  const [shippingPresets, setShippingPresets] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/shipping-presets").then(({ data }) => {
      if (alive) setShippingPresets(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        setProduct(data);
        setConfig({
          // Default quantity = smallest available bracket (anchors) or smallest
          // tier_pricing row, whichever the product uses. Falls back to 100
          // for products with neither (configurator default).
          quantity: (() => {
            if (data.label_model?.quantity_breaks?.length) return data.label_model.quantity_breaks[0];
            const brackets = data.size_anchor_brackets || [];
            const hasAnchors = (data.size_anchors || []).length >= 2;
            if (hasAnchors && brackets.length > 0) {
              return Math.min(...brackets.map((n) => Number(n) || 0).filter((n) => n > 0)) || 100;
            }
            return data.tier_pricing?.[0]?.min_qty || 100;
          })(),
          material_key: data.materials?.[0]?.key || "",
          // sizes can be string[] (legacy) or [{key,name,percent_uplift}]
          // (new). Normalize the initial selection to a string so the
          // <select> and downstream code never receive an object.
          size: (() => {
            const s0 = data.sizes?.[0];
            if (!s0) return "";
            return typeof s0 === "string" ? s0 : (s0.key || s0.name || "");
          })(),
          shape: data.shapes?.[0] || "",
          // Two color shapes coexist: structured `color_options` (new,
          // sends color_key) or legacy `colors: string[]` (sends color).
          // Initialize both so neither path is undefined on first calc.
          color: (data.color_options?.[0]?.name) || data.colors?.[0] || "",
          color_key: data.color_options?.[0]?.key || "",
          adhesive_key: data.adhesives?.[0]?.key || "",
          corners: data.corners?.includes("Square") ? "Square" : (data.corners?.[0] || ""),
          height_in: data.custom_dimensions?.default_height ? String(data.custom_dimensions.default_height) : "",
          width_in: data.custom_dimensions?.default_width ? String(data.custom_dimensions.default_width) : "",
          lamination_key: (data.laminations || []).find((l) => /none|no lam/i.test(l.name || l.label || l.key))?.key || data.laminations?.[0]?.key || "none",
          varnish_key: data.varnishes?.[0]?.key || "none",
          foil_key: (data.foils || []).find((f) => /^none/i.test(f.name || f.label || f.key))?.key || data.foils?.[0]?.key || "none",
          pole_pocket_key: data.pole_pockets?.[0]?.key || "none",
          hem_key: data.hems?.[0]?.key || "none",
          grommet_key: data.grommets?.[0]?.key || "none",
          // Default each finishing-group dropdown to its first choice.
          finishing_selections: Object.fromEntries(
            (data.finishing_groups || []).map((g) => [g.key, g.choices?.[0]?.key || "none"])
          ),
          core_size: data.roll_parameters?.core_sizes?.[0] || "",
          labels_per_roll: data.roll_parameters?.labels_per_roll?.[0] ? String(data.roll_parameters.labels_per_roll[0]) : "",
          unwind_direction: data.roll_parameters?.unwind_directions?.[0] || "",
          outside_diameter: data.roll_parameters?.outside_diameters?.[0] || "",
        });
        setActiveImage(0);
        // Size handed over from a calculator (?w=&h=) and tool attribution (?tool=).
        // Params are kept in a ref so React StrictMode's double effect run (and our URL clean-up) can't drop them.
        if (window.location.search) handoffRef.current = { slug, qs: new URLSearchParams(window.location.search) };
        else if (handoffRef.current?.slug !== slug) handoffRef.current = null;
        const qs = handoffRef.current?.qs || new URLSearchParams();
        if (qs.get("tool")) rememberToolSource(qs.get("tool"));
        const qw = parseFloat(qs.get("w")), qh = parseFloat(qs.get("h"));
        if (qw > 0 && qh > 0 && (data.custom_dimensions || data.size_input_mode === "sqft")) {
          applySizeFromTool(qw, qh, data, !!qs.get("from"));
        }
        // "Switch and keep": quantity / shape / finishes carried over from another label stock (?qty=&shape=&lam=&foil=)
        if (qs.get("qty") || qs.get("shape") || qs.get("lam") || qs.get("foil")) {
          const fam = (n) => { n = (n || "").toLowerCase(); return /rose/.test(n) ? "rose" : /holo/.test(n) ? "holo" : /gold/.test(n) ? "gold" : /silver/.test(n) ? "silver" : /soft/.test(n) ? "soft" : /matte/.test(n) ? "matte" : /gloss|uv/.test(n) ? "gloss" : n; };
          const byName = (list, name) => ((list || []).find((o) => (o.name || o.label || o.key || "").toLowerCase() === (name || "").toLowerCase())
            || (list || []).find((o) => fam(o.name || o.label || o.key) === fam(name)))?.key;
          const noneKey = (list, re) => (list || []).find((o) => re.test(o.name || o.label || o.key || ""))?.key;
          const qty = parseInt(qs.get("qty"), 10);
          const breaks = data.label_model?.quantity_breaks;
          setConfig((c) => ({
            ...c,
            quantity: qty > 0 ? (breaks?.length ? (breaks.includes(qty) ? qty : breaks.reduce((b, x) => (Math.abs(x - qty) < Math.abs(b - qty) ? x : b), breaks[0])) : qty) : c.quantity,
            shape: qs.get("shape") && (data.shapes || []).includes(qs.get("shape")) ? qs.get("shape") : c.shape,
            lamination_key: qs.has("lam") && data.laminations?.length ? (qs.get("lam") ? byName(data.laminations, qs.get("lam")) || c.lamination_key : noneKey(data.laminations, /none|no lam/i) || c.lamination_key) : c.lamination_key,
            foil_key: qs.has("foil") && data.foils?.length ? (qs.get("foil") ? byName(data.foils, qs.get("foil")) || c.foil_key : noneKey(data.foils, /^none/i) || c.foil_key) : c.foil_key,
          }));
          if (qs.get("from")) toast.success(`Switched from ${qs.get("from")} — same size, quantity and finishes kept`);
          // keep the canonical, param-free URL in the address bar (SEO: one URL per product)
          window.history.replaceState(null, "", window.location.pathname);
        }
        // Pre-select the cheapest turnaround tier so the picker has a
        // sensible default (typically "Standard · included").
        const opts = data.turnaround_options || [];
        if (opts.length) {
          const cheapest = [...opts].sort(
            (a, b) => (Number(a.price_value) || 0) - (Number(b.price_value) || 0)
          )[0];
          setTurnaroundKey(cheapest?.key || "");
        } else {
          setTurnaroundKey("");
        }
      } catch (e) {
        toast.error("Product not found");
        navigate("/shop");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate, reloadKey]);

  // Foil color chart picker: only when the admin turned it on, and only while a foil is actually selected.
  const foilPickerVisible = useMemo(() => {
    if (!product?.foil_chart_enabled) return false;
    const none = (n) => /^none|^no foil/i.test(String(n || ""));
    if (product.foils?.length) return !none(product.foils.find((f) => f.key === config.foil_key)?.name);
    const grp = (product.custom_option_groups || []).find((g) => /foil/i.test(g.label || g.name || ""));
    if (grp) {
      const sel = (config.custom_options || {})[grp.key] || grp.items?.[0]?.key;
      return !none(grp.items?.find((i) => i.key === sel)?.name);
    }
    return true;
  }, [product, config.foil_key, config.custom_options]);
  // Colorplan shade picker: admin switch + a "Colorplan" material/option selected + foil picker active.
  const colorplanPickerVisible = useMemo(() => {
    if (!product?.colorplan_picker_enabled || !foilPickerVisible) return false;
    const isCp = (n) => /colorplan/i.test(String(n || ""));
    if (isCp(product.materials?.find((m) => m.key === config.material_key)?.name)) return true;
    return (product.custom_option_groups || []).some((g) => {
      const sel = (config.custom_options || {})[g.key] || g.items?.[0]?.key;
      return isCp(g.items?.find((i) => i.key === sel)?.name);
    });
  }, [product, foilPickerVisible, config.material_key, config.custom_options]);
  const colorplanActive = colorplanPickerVisible && colorplanShade;
  const computePrice = useCallback(async () => {
    if (!product || !config.material_key) return;
    setCalcing(true);
    try {
      const isLargeFormat = LARGE_FORMAT_CATEGORIES.has(product.service_category);
      // For large-format, the displayed price covers ALL jobs combined.
      const totalQty = isLargeFormat
        ? Math.max(1, jobs.reduce((s, j) => s + (parseInt(j.quantity) || 0), 0))
        : config.quantity;
      const body = {
        product_id: product.product_id,
        quantity: totalQty,
        material_key: config.material_key,
      };
      if (product.adhesives?.length && config.adhesive_key) body.adhesive_key = config.adhesive_key;
      if (config.shape) body.shape = config.shape;
      if (product.laminations?.length && config.lamination_key) body.lamination_key = config.lamination_key;
      if (product.varnishes?.length && config.varnish_key) body.varnish_key = config.varnish_key;
      if (product.foils?.length && config.foil_key) body.foil_key = config.foil_key;
      if (product.pole_pockets?.length && config.pole_pocket_key) body.pole_pocket_key = config.pole_pocket_key;
      if (product.hems?.length && config.hem_key) body.hem_key = config.hem_key;
      if (product.grommets?.length && config.grommet_key) body.grommet_key = config.grommet_key;
      if (product.finishing_groups?.length && config.finishing_selections) {
        body.finishing_selections = config.finishing_selections;
      }
      // Color: prefer the new structured color_options (sends `color_key`)
      // and fall back to the legacy name-based path for un-migrated products.
      if (product.color_options?.length && config.color_key) {
        body.color_key = config.color_key;
      } else if (product.color_surcharges && config.color) {
        body.color = config.color;
      }
      if (product.custom_dimensions || product.size_input_mode === "sqft") {
        // Both custom-W×H and sq-ft modes send raw inches in the payload.
        // The backend differentiates by `size_input_mode` on the product.
        body.height_in = parseFloat(config.height_in) || (product.custom_dimensions?.default_height ?? 0);
        body.width_in = parseFloat(config.width_in) || (product.custom_dimensions?.default_width ?? 0);
      } else if (config.size) {
        // Preset size dropdown — send the row's key/name so the backend
        // can look up the matching `percent_uplift` and scale the unit
        // price accordingly. Previously this was never sent, leaving the
        // price stuck at the baseline regardless of the selected preset.
        body.size_key = config.size;
      }
      if (selectedAddons.length) body.addon_keys = selectedAddons;
      if (turnaroundKey) body.turnaround_key = turnaroundKey;
      // Custom option groups (iter18) — book Size, Page Count, Cover
      // Material, etc. The backend matches each selection key against the
      // product's custom_option_groups and applies base/surcharge math.
      if (config.custom_options && Object.keys(config.custom_options).length) {
        body.custom_option_selections = config.custom_options;
      }
      if (config.hang_tag_finishing) body.hang_tag_finishing = config.hang_tag_finishing;
      if (colorplanActive) body.colorplan_shade = colorplanShade;
      const { data } = await api.post("/products/price-calc", body);
      setPrice(data);
    } catch (e) {
      console.error(e);
    } finally {
      setCalcing(false);
    }
  }, [product, config, selectedAddons, jobs, turnaroundKey, colorplanActive, colorplanShade]);

  useEffect(() => { computePrice(); }, [computePrice]);

  // ── SEO: real parent category name + structured data ──────────────────
  // Pull the live service-category list so the visible breadcrumb and the
  // BreadcrumbList schema use the REAL parent category (e.g. "Booklets &
  // Books") instead of the legacy `product.category` string (which is just a
  // colour tag like "Color").
  const { categories: liveCategories } = useServiceCategories();
  const parentCategory = useMemo(() => {
    if (!product) return null;
    const slugMatch = liveCategories.find((c) => c.slug === product.service_category);
    if (slugMatch) return { name: slugMatch.name, slug: slugMatch.slug };
    if (product.service_category) {
      // Fallback: humanise the slug ("digital-print" → "Digital Print")
      return {
        name: product.service_category
          .split("-")
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
          .join(" "),
        slug: product.service_category,
      };
    }
    return null;
  }, [product, liveCategories]);

  const seoPath = product ? `/shop/product/${product.slug || product.product_id}` : "";
  const seoImage = product ? productImage((product.images && product.images[0]) || product.image_url || "") : "";
  const productMetaTitle = product
    ? (product.meta_title || `${product.name} — Kolt Printing`)
    : "Kolt Printing";
  const productMetaDesc = product
    ? (product.meta_description || product.short_description || product.description || "")
    : "";

  const productJsonLd = useMemo(() => {
    if (!product) return null;
    return {
      id: "product",
      data: productSchema({
        product,
        url: seoPath,
        image: seoImage,
        price,
      }),
    };
  }, [product, seoPath, seoImage, price]);

  const breadcrumbJsonLd = useMemo(() => {
    if (!product) return null;
    const crumbs = [
      { name: "Home", url: "/" },
      { name: "Shop", url: "/shop" },
    ];
    if (parentCategory) crumbs.push({ name: parentCategory.name, url: `/shop/${parentCategory.slug}` });
    crumbs.push({ name: product.name, url: seoPath });
    return { id: "breadcrumb", data: breadcrumbSchema(crumbs) };
  }, [product, parentCategory, seoPath]);

  const faqItems = useMemo(() => {
    if (!product) return [];
    const parsed = splitSeoBody(product.seo_content).faqs || [];
    return buildProductFaqs({ ...product, faqs: [...(product.faqs || []), ...parsed] });
  }, [product]);
  const faqJsonLd = useMemo(() => {
    const data = faqJsonLdFor(faqItems);
    return data ? { id: "faq", data } : null;
  }, [faqItems]);

  useSeoHead({
    title: productMetaTitle,
    description: productMetaDesc,
    canonical: seoPath,
    image: seoImage,
    ogType: "product",
    jsonLd: [productJsonLd, breadcrumbJsonLd, faqJsonLd].filter(Boolean),
  });

  const currentMaterial = useMemo(() => product?.materials?.find((m) => m.key === config.material_key), [product, config.material_key]);
  const currentAdhesive = useMemo(() => product?.adhesives?.find((a) => a.key === config.adhesive_key), [product, config.adhesive_key]);

  const addToCart = () => {
    if (!price) return;
    const isLargeFormat = LARGE_FORMAT_CATEGORIES.has(product.service_category);
    const selectedTurnaround = (product.turnaround_options || []).find((t) => t.key === turnaroundKey) || null;
    const turnaroundCartInfo = selectedTurnaround
      ? {
          key: selectedTurnaround.key,
          label: selectedTurnaround.label,
          business_days: selectedTurnaround.business_days || "",
          price_mode: selectedTurnaround.price_mode || "percent",
          price_value: Number(selectedTurnaround.price_value) || 0,
          surcharge: Number(price?.turnaround_surcharge) || 0,
        }
      : null;

    // Engine-ready selection keys. These must ride along on EVERY cart line:
    // checkout re-prices the order server-side, and any selection we don't
    // pass by key (add-ons, turnaround, colour, finishing groups, custom
    // option groups) gets silently dropped from the order + invoice.
    const spec = {};
    if (foilPickerVisible) spec.foil_code = foilCode || FOIL_RECOMMEND;
    if (colorplanPickerVisible) spec.colorplan_shade = colorplanShade || "";
    if (colorplanPickerVisible && !colorplanShade) spec.colorplan_note = COLORPLAN_RECOMMEND;
    if (!(product.custom_dimensions || product.size_input_mode === "sqft") && config.size) {
      spec.size_key = config.size;
      const sizeRow = (product.sizes || []).find(
        (s) => typeof s === "object" && (s.key === config.size || s.name === config.size)
      );
      spec.size_name = sizeRow?.name || (typeof config.size === "string" && !config.size.startsWith("size_") ? config.size : "");
    }
    if (config.color_key) spec.color_key = config.color_key;
    if (selectedAddons.length) spec.addon_keys = [...selectedAddons];
    if (turnaroundKey) spec.turnaround_key = turnaroundKey;
    if (product.adhesives?.length) {
      spec.adhesive_key = config.adhesive_key;
      spec.adhesive_name = currentAdhesive?.name || "";
    }
    for (const [listKey, cfgKey, nameKey] of [
      ["laminations", "lamination_key", "lamination_name"],
      ["varnishes", "varnish_key", "varnish_name"],
      ["foils", "foil_key", "foil_name"],
      ["pole_pockets", "pole_pocket_key", "pole_pocket_name"],
      ["hems", "hem_key", "hem_name"],
      ["grommets", "grommet_key", "grommet_name"],
    ]) {
      const chosen = config[cfgKey];
      if (product[listKey]?.length && chosen && chosen !== "none") {
        spec[cfgKey] = chosen;
        spec[nameKey] = product[listKey].find((o) => o.key === chosen)?.name || "";
      }
    }
    if (product.finishing_groups?.length) {
      const keys = Object.fromEntries(
        Object.entries(config.finishing_selections || {}).filter(([, v]) => v && v !== "none")
      );
      if (Object.keys(keys).length) spec.finishing_selections = keys;
    }
    // Display breakdowns straight from the pricing engine (group name, choice
    // name and the surcharge that was charged) so the cart, order and invoice
    // all show the same wording the customer picked.
    if (price?.finishing && Object.keys(price.finishing).length) spec.finishing = price.finishing;
    if (config.custom_options && Object.keys(config.custom_options).length) {
      spec.custom_option_selections = { ...config.custom_options };
    }
    if (price?.custom_options?.length) spec.custom_options = price.custom_options;
    if (config.hang_tag_finishing) spec.hang_tag_finishing = config.hang_tag_finishing;
    if (price?.hang_tag_finishing) spec.hang_tag_finishing_priced = price.hang_tag_finishing;
    // Multi-job pattern for large format: one cart line per job row.
    if (isLargeFormat) {
      const validJobs = jobs.filter((j) => j.job_name.trim() && j.quantity > 0);
      if (validJobs.length === 0) {
        toast.error("Add at least one job with a Job Name / PO#");
        return;
      }
      const missingArtwork = validJobs.filter((j) => !j.artwork?.file_id);
      if (missingArtwork.length > 0) {
        toast.error(`Upload artwork for ${missingArtwork.length} job${missingArtwork.length > 1 ? "s" : ""}`);
        return;
      }
      const unitPrice = price.unit_price; // flat per-unit, no tier change
      // Compute per-line addons total to attach to each job's line_total.
      const selectedAddonSpecs = (product.addons || []).filter((a) => selectedAddons.includes(a.key));
      const totalJobsQty = validJobs.reduce((s, j) => s + j.quantity, 0);
      const turnaroundFlat = turnaroundCartInfo?.price_mode === "flat" ? turnaroundCartInfo.price_value : 0;
      const turnaroundPct  = turnaroundCartInfo?.price_mode === "percent" ? turnaroundCartInfo.price_value : 0;
      for (const j of validJobs) {
        const baseLine = unitPrice * j.quantity;
        const addonsLine = selectedAddonSpecs.reduce(
          (s, a) => s + (a.mode === "per_unit" ? a.price * j.quantity : a.price),
          0
        );
        // Turnaround: percent uplifts uniformly; flat splits proportionally by qty so the cart total still equals the displayed line_total.
        const turnaroundLine = turnaroundPct
          ? (baseLine + addonsLine) * (turnaroundPct / 100)
          : turnaroundFlat * (totalJobsQty ? j.quantity / totalJobsQty : 0);
        const lineTotal = Number((baseLine + addonsLine + turnaroundLine).toFixed(2));
        addItem({
          product_id: product.product_id,
          product_name: product.name,
          image_url: product.image_url,
          quantity: j.quantity,
          material_key: config.material_key,
          material_name: currentMaterial?.name || "",
          size: product.custom_dimensions ? `${config.height_in}" × ${config.width_in}"` : config.size,
          shape: config.shape,
          color: config.color,
          unit_price: unitPrice,
          line_total: lineTotal,
          addons: selectedAddonSpecs.map((a) => ({
            key: a.key, name: a.name, price: a.price, mode: a.mode,
          })),
          artwork_filename: j.artwork?.source_filename || j.artwork?.original_filename || null,
          artwork_file_id: j.artwork?.file_id || null,
          production_notes: productionNotes || null,
          job_name: j.job_name.trim(),
          turnaround: turnaroundCartInfo,
          ...spec,
        });
      }
      toast.success(`${validJobs.length} job${validJobs.length > 1 ? "s" : ""} added to cart`);
      return;
    }

    const item = {
      product_id: product.product_id,
      product_name: product.name,
      image_url: product.image_url,
      quantity: config.quantity,
      material_key: config.material_key,
      material_name: currentMaterial?.name || "",
      size: product.custom_dimensions ? `${config.height_in}" × ${config.width_in}"` : config.size,
      shape: config.shape,
      color: config.color,
      unit_price: price.unit_price,
      line_total: price.line_total,
      artwork_filename: uploadedFile?.source_filename || uploadedFile?.original_filename || null,
      artwork_file_id: uploadedFile?.file_id || null,
      production_notes: productionNotes || null,
      addons: (product.addons || [])
        .filter((a) => selectedAddons.includes(a.key))
        .map((a) => ({ key: a.key, name: a.name, price: a.price, mode: a.mode })),
      turnaround: turnaroundCartInfo,
      ...spec,
    };
    if (product.roll_parameters) {
      if (config.core_size) item.core_size = config.core_size;
      if (config.labels_per_roll) item.labels_per_roll = parseInt(config.labels_per_roll);
      if (config.unwind_direction) item.unwind_direction = config.unwind_direction;
      if (config.outside_diameter) item.outside_diameter = config.outside_diameter;
    }
    if (product.corners?.length) item.corners = config.corners;
    if (product.custom_dimensions) {
      item.height_in = parseFloat(config.height_in);
      item.width_in = parseFloat(config.width_in);
    }
    addItem(item);
    toast.success("Added to cart");
    if (toolSource()) trackTool(toolSource(), "add_to_cart", { product: product.slug, total: item.line_total });
  };

  const saveQuote = async () => {
    if (!user) { toast.error("Sign in to save quotes"); navigate("/login"); return; }
    if (!price) return;
    setSaving(true);
    try {
      await api.post("/saved-configs", {
        product_id: product.product_id,
        product_name: product.name,
        image_url: product.image_url,
        quantity: config.quantity,
        material_key: config.material_key,
        material_name: currentMaterial?.name || "",
        size: product.custom_dimensions ? `${config.height_in}" × ${config.width_in}"` : config.size,
        shape: config.shape,
        color: config.color,
        unit_price: price.unit_price,
        line_total: price.line_total,
      });
      toast.success("Saved to your quotes");
    } catch (e) {
      toast.error("Could not save quote");
    } finally {
      setSaving(false);
    }
  };

  if (!product) return <div className="container-page py-20 text-center text-slate-500">Loading product...</div>;
  if (product.layout === "box-configurator") return <BoxProductPage key={product.slug} product={product} />;

  const gallery = product.image_gallery?.length ? product.image_gallery : [product.image_url];
  // Wedding products get the same warm, tan "stationery" palette as the
  // /wedding collection landing page (bg #FBF8F3, warm #E8D9C3 card borders).
  const isWedding = product.sub_category === "wedding-collateral";
  const pageBgClass = isWedding ? "bg-[#FBF8F3]" : "bg-slate-50";
  const cardClass = isWedding
    ? "bg-white border border-[#E8D9C3] rounded-none transition-colors duration-200 hover:border-[#2C3340]"
    : "card-base";
  const hasCustomDims = !!product.custom_dimensions;
  const inTemplateRange = (n) => Number(n) >= 0.5 && Number(n) <= 240;
  const customSizePreset =
    hasCustomDims && inTemplateRange(config.width_in) && inTemplateRange(config.height_in)
      ? [{
          label: `${product.name} · your size ${config.width_in}" × ${config.height_in}"`,
          w: Number(config.width_in),
          h: Number(config.height_in),
          bleed: Math.max(Number(config.width_in), Number(config.height_in)) >= 24 ? 0.25 : 0.125,
        }]
      : [];
  const templatePresets = [...customSizePreset, ...getTemplatePresetsForProduct(product)];
  const showTemplatesTab = templatePresets.length > 0;

  const breadcrumbs = (
      <div className="container-page py-4 text-xs text-slate-500" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#333F48]">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-[#333F48]">Shop</Link>
        {parentCategory && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/shop/${parentCategory.slug}`} className="hover:text-[#333F48]" data-testid="breadcrumb-parent">
              {parentCategory.name}
            </Link>
          </>
        )}
        <span className="mx-2">/</span>
        <span className="text-slate-700 font-semibold">{product.name}</span>
      </div>
  );

  const optName = (list, key) => (list || []).find((o) => o.key === key)?.name || (list || []).find((o) => o.key === key)?.label || "";
  const labelQuoteSpec = product.label_model ? {
    product: product.name, width_in: config.width_in, height_in: config.height_in, shape: config.shape,
    material: optName(product.materials, config.material_key),
    lamination: /none|no lam/i.test(optName(product.laminations, config.lamination_key)) ? "" : optName(product.laminations, config.lamination_key),
    foil: /^none/i.test(optName(product.foils, config.foil_key)) ? "" : optName(product.foils, config.foil_key),
  } : null;
  const galleryCard = (
          <div className={`${cardClass} overflow-hidden`}>
            <div className="aspect-[4/3] bg-slate-100 relative">
              <img src={productThumb(gallery[activeImage], 1200)} alt={product.name} fetchPriority="high" decoding="async" className="w-full h-full object-cover" data-testid="product-hero-image" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 p-3 border-t border-slate-100" data-testid="image-thumbs">
                {gallery.map((g, i) => (
                  <button
                    key={g}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors ${i === activeImage ? "border-[#333F48]" : "border-transparent hover:border-slate-300"}`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={productThumb(g, 160)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="p-6">
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="badge-navy">{product.category}</span>
                {product.is_featured && <span className="badge-accent">Featured</span>}
                <span className="badge-outline font-mono">SKU {product.sku_prefix}-{product.product_id.slice(-5).toUpperCase()}</span>
                <MadeInUSABadge />
              </div>
              <OwnerEditButton user={user} onClick={() => setOwnerEditing(true)} />
              <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight mb-3">
                {product.name}
              </h1>
              {Number(product.rating_count) > 0 && (
                <Link to="/reviews" className="inline-flex items-center gap-1.5 mb-3 text-sm text-slate-700 hover:text-kolt-orange" data-testid="product-rating">
                  <span className="text-[#DD6B33] tracking-tight" aria-hidden="true">
                    {"★".repeat(Math.round(Number(product.rating_value)))}{"☆".repeat(5 - Math.round(Number(product.rating_value)))}
                  </span>
                  <span className="font-semibold">{Number(product.rating_value).toFixed(1)}</span>
                  <span className="text-slate-500">({product.rating_count} verified {product.rating_count === 1 ? "review" : "reviews"})</span>
                </Link>
              )}
              <SeoProse text={cleanDescription(product.description, product.name)} className="text-base text-slate-600" testId="product-description" />
            </div>
          </div>
  );

  // SEO body content — long-form copy below the fold. Q:/A: pairs become an
  // accordion (which doubles as FAQPage rich-result eligibility via JSON-LD).
  const seoBody = (() => {
            if (!product.seo_content) return faqItems.length ? <FaqAccordion items={faqItems} /> : null;
            const split = splitSeoBody(product.seo_content);
            if (split.faqs.length === 0) {
              return (
                <>
                  <MoreAboutProduct text={product.seo_content} cardClass={cardClass} />
                  {faqItems.length > 0 && <FaqAccordion items={faqItems} />}
                </>
              );
            }
            return (
              <>
                <MoreAboutProduct text={split.prefix} cardClass={cardClass} />
                <FaqAccordion items={faqItems} />
                <MoreAboutProduct text={split.suffix} testid="seo-body-suffix" cardClass={cardClass} />
              </>
            );
  })();

  const tabsCard = (
          <Tabs defaultValue="features" className={cardClass}>
            <TabsList className="rounded-none border-b border-slate-200 bg-slate-50 w-full justify-start px-6 h-12">
              <TabsTrigger value="features" className="rounded-sm" data-testid="tab-features">Features</TabsTrigger>
              <TabsTrigger value="pricing-table" className="rounded-sm" data-testid="tab-pricing">Volume Pricing</TabsTrigger>
              <TabsTrigger value="materials" className="rounded-sm" data-testid="tab-materials">Materials</TabsTrigger>
              {product.adhesives?.length > 0 && (
                <TabsTrigger value="adhesives" className="rounded-sm" data-testid="tab-adhesives">Adhesives</TabsTrigger>
              )}
              {showTemplatesTab && (
                <TabsTrigger value="templates" className="rounded-sm" data-testid="tab-templates">Templates</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="features" className="p-6">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-[#DD6B33] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="pricing-table" className="p-0">
              {price?.ladder ? (
                <LabelPriceLadder price={price} quantity={config.quantity} spec={labelQuoteSpec} onPick={(q) => setConfig((c) => ({ ...c, quantity: q }))} />
              ) : (product.tier_pricing || []).length === 0 ? (
                <div className="p-6 text-sm text-slate-500" data-testid="tier-pricing-empty">
                  Volume pricing scales by your selected dimensions — pick a Width × Height and a Quantity above to see your live price.
                </div>
              ) : (
              <table className="data-table" data-testid="tier-pricing-table">
                <thead>
                  <tr>
                    <th>Min Quantity</th>
                    <th>Unit Price</th>
                    <th>Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {product.tier_pricing.map((t, idx) => {
                    const top = product.tier_pricing[0].unit_price;
                    const savings = idx === 0 ? 0 : Math.round(((top - t.unit_price) / top) * 100);
                    return (
                      <tr key={t.min_qty}>
                        <td className="font-mono font-bold">{t.min_qty.toLocaleString()}+</td>
                        <td className="font-display font-bold text-[#333F48]">{moneyUnit(t.unit_price)}</td>
                        <td className="text-emerald-700 font-bold">{savings > 0 ? `${savings}% off` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
              <div className="p-4 text-xs text-slate-500 border-t border-slate-200">
                Base prices shown for 2"×2" (reference area). Final price scales by your selected material, adhesive, and dimensions.
              </div>
            </TabsContent>
            <TabsContent value="materials" className="p-6 space-y-3">
              {product.materials.map((m) => (
                <div key={m.key} className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-slate-900">{m.name}</div>
                    <span className="badge-outline font-mono">{formatModifier(m)}</span>
                  </div>
                  {m.description && <div className="text-sm text-slate-500 mt-1">{m.description}</div>}
                </div>
              ))}
            </TabsContent>
            {product.adhesives?.length > 0 && (
              <TabsContent value="adhesives" className="p-6 space-y-3">
                {product.adhesives.map((a) => (
                  <div key={a.key} className="border border-slate-200 rounded-sm p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-slate-900">{a.name}</div>
                      <span className="badge-outline font-mono">{formatModifier(a)}</span>
                    </div>
                    {a.description && <div className="text-sm text-slate-500 mt-1">{a.description}</div>}
                  </div>
                ))}
              </TabsContent>
            )}
            {showTemplatesTab && (
              <TabsContent value="templates" className="p-6" data-testid="tab-templates-content">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                  <div>
                    <div className="font-display font-black text-lg text-[#333F48] leading-tight">
                      Print-ready design templates
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5">
                      {templatePresets.some((p) => p.file) ? "Vendor dieline with panel layout, bleed and safe-area marked · CMYK · PDF" : "One-click PDF at 1:1 scale · CMYK · trim + bleed + safe-area + registration marks"}
                    </div>
                  </div>
                  <span className="badge-outline font-mono text-[10px] self-start sm:self-auto">
                    Free download
                  </span>
                </div>
                <ul className="divide-y divide-slate-200 border border-slate-200 rounded-sm" data-testid="design-template-list">
                  {templatePresets.map((p, idx) => (
                    <li key={`${p.w}x${p.h}-${p.label}`}>
                      {p.group && p.group !== templatePresets[idx - 1]?.group && (
                        <div className="px-4 pt-3 pb-1 text-[11px] font-mono uppercase tracking-[0.18em] text-[#DD6B33] font-bold bg-slate-50 border-b border-slate-100" data-testid={`template-group-${idx}`}>{p.group}</div>
                      )}
                      <a
                        href={templateHref(API, product.slug, p)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          try {
                            api.post("/marketing/events/template-download", {
                              product_id: product.product_id,
                              product_slug: product.slug,
                              product_name: product.name,
                              sku: `${product.sku_prefix}-${p.w}x${p.h}`,
                              width_in: p.w,
                              height_in: p.h,
                              fmt: "pdf",
                              shape: "Rectangle",
                              corners: "Square",
                              material_name: "",
                              color: config.color || "",
                              source: "templates_tab",
                            }).catch(() => {});
                          } catch { /* noop */ }
                        }}
                        className="group flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                        data-testid={`template-row-${p.w}x${p.h}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {p.file && !p.guide ? (
                            <img
                              src={`${API}/templates/thumb?src=${encodeURIComponent(p.file)}`}
                              alt=""
                              loading="lazy"
                              width="72" height="52"
                              className="w-[72px] h-[52px] object-contain bg-white border border-slate-200 rounded-sm shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                              data-testid={`template-thumb-${idx}`}
                            />
                          ) : (
                          <div className="flex items-center justify-center w-9 h-9 rounded-sm bg-slate-100 group-hover:bg-[#333F48] transition-colors shrink-0">
                            {p.guide ? <BookOpen className="w-4 h-4 text-slate-600 group-hover:text-white" /> : <FileText className="w-4 h-4 text-slate-600 group-hover:text-white" />}
                          </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 text-sm truncate">{p.short || p.label}</div>
                            <div className="text-[11px] font-mono text-slate-500">
                              {p.guide ? "Step-by-step setup & care · PDF" : p.file ? "Print-ready dieline · PDF" : <>{p.w}&quot; × {p.h}&quot; · {p.bleed}&quot; bleed · PDF</>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 group-hover:text-[#DD6B33] shrink-0">
                          Download
                          <Download className="w-4 h-4" />
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 p-3 border border-slate-200 rounded-sm bg-slate-50 text-xs text-slate-600 leading-relaxed">
                  <span className="font-bold text-slate-900">Designer checklist:</span> CMYK color mode ·
                  fonts outlined or embedded · raster art at 300 dpi (100 dpi for banners &gt; 24&quot;) ·
                  export as PDF/X-4 with all layers flattened · extend background art to the magenta bleed line.
                  Every template PDF has these rules printed inside the file so nothing gets missed.
                </div>
              </TabsContent>
            )}
          </Tabs>
  );

  const specBlock = (
    <>
            <ProductOptions product={product} config={config} setConfig={setConfig} />
            {foilPickerVisible && <FoilCodePicker value={foilCode} onChange={setFoilCode} />}
            {colorplanPickerVisible && <ColorplanShadePicker value={colorplanShade} onChange={setColorplanShade} foilCode={foilCode} surchargePerPiece={product.colorplan_surcharge_per_piece} />}

            {/* Live size preview — shown when product has custom dimensions */}
            {hasCustomDims && (
              <div className="mt-5 space-y-2">
                <LiveSizePreview
                  shape={config.shape}
                  corners={config.corners}
                  heightIn={config.height_in}
                  widthIn={config.width_in}
                  color={config.color}
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await generateDieTemplate({
                        shape: config.shape,
                        corners: config.corners,
                        heightIn: config.height_in,
                        widthIn: config.width_in,
                        productName: product.name,
                        materialName: currentMaterial?.name || "",
                        color: config.color,
                        sku: `${product.sku_prefix}-${product.product_id.slice(-5).toUpperCase()}`,
                      });
                      logTemplateDownload({
                        product_id: product.product_id,
                        product_slug: product.slug,
                        product_name: product.name,
                        sku: `${product.sku_prefix}-${product.product_id.slice(-5).toUpperCase()}`,
                        width_in: parseFloat(config.width_in) || 0,
                        height_in: parseFloat(config.height_in) || 0,
                        fmt: "pdf",
                        shape: config.shape,
                        corners: config.corners,
                        material_name: currentMaterial?.name || "",
                        color: config.color,
                        source: "product_page",
                      });
                      toast.success("Die template downloaded");
                    } catch (e) {
                      console.error(e);
                      toast.error("Could not generate template");
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 border-2 border-slate-300 hover:border-[#333F48] hover:bg-slate-50 rounded-sm py-2.5 text-sm font-bold text-slate-700 hover:text-[#333F48] transition-colors"
                  data-testid="download-die-template-btn"
                >
                  <Download className="w-4 h-4" />
                  Download Die Template (PDF, 1:1 scale)
                </button>
                <div className="flex items-stretch gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        generateDieSvg({
                          shape: config.shape,
                          corners: config.corners,
                          heightIn: config.height_in,
                          widthIn: config.width_in,
                          productName: product.name,
                          materialName: currentMaterial?.name || "",
                          color: config.color,
                          sku: `${product.sku_prefix}-${product.product_id.slice(-5).toUpperCase()}`,
                        });
                        logTemplateDownload({
                          product_id: product.product_id,
                          product_slug: product.slug,
                          product_name: product.name,
                          sku: `${product.sku_prefix}-${product.product_id.slice(-5).toUpperCase()}`,
                          width_in: parseFloat(config.width_in) || 0,
                          height_in: parseFloat(config.height_in) || 0,
                          fmt: "svg",
                          shape: config.shape,
                          corners: config.corners,
                          material_name: currentMaterial?.name || "",
                          color: config.color,
                          source: "product_page",
                        });
                        toast.success("SVG template downloaded — opens in Illustrator & CorelDRAW");
                      } catch (e) {
                        console.error(e);
                        toast.error("Could not generate SVG");
                      }
                    }}
                    className="flex-1 flex items-center justify-center gap-2 border-2 border-slate-300 hover:border-[#333F48] hover:bg-slate-50 rounded-sm py-2.5 text-sm font-bold text-slate-700 hover:text-[#333F48] transition-colors"
                    data-testid="download-die-svg-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download SVG (Illustrator / CorelDRAW)
                  </button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label="How to open the SVG in Illustrator or CorelDRAW"
                        className="flex items-center justify-center px-2 border-2 border-slate-300 hover:border-[#333F48] hover:bg-slate-50 rounded-sm text-slate-600 hover:text-[#333F48] transition-colors"
                        data-testid="svg-help-btn"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="top"
                      align="end"
                      className="w-80 rounded-sm border-slate-300 p-0 shadow-lg"
                      data-testid="svg-help-popover"
                    >
                      <div className="bg-kolt-slate-dark text-white px-4 py-2.5 rounded-t-sm">
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-kolt-orange">
                          Template Guide
                        </div>
                        <div className="font-display font-bold text-[13px] mt-0.5">
                          Opening the SVG template
                        </div>
                      </div>
                      <div className="p-4 space-y-3 text-[13px] text-slate-700 leading-relaxed">
                        <div>
                          <div className="font-bold text-slate-900 mb-1">Adobe Illustrator (.ai)</div>
                          <div className="text-slate-600">
                            File → Open → select the .svg. Layers (<span className="font-mono text-[11px]">Bleed</span>,{" "}
                            <span className="font-mono text-[11px]">Die-Cut-Line</span>,{" "}
                            <span className="font-mono text-[11px]">Safe-Zone</span>) appear in the Layers panel — lock the die layer and design underneath. Save as <span className="font-mono text-[11px]">.ai</span> when finished.
                          </div>
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 mb-1">CorelDRAW (.cdr)</div>
                          <div className="text-slate-600">
                            File → Import → select the .svg → choose <span className="font-mono text-[11px]">Curves</span> on import. Layers land in the Object Manager. Save as <span className="font-mono text-[11px]">.cdr</span> when finished.
                          </div>
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 mb-1">Affinity Designer / Inkscape</div>
                          <div className="text-slate-600">Open the .svg directly — layers and dashed lines are preserved.</div>
                        </div>
                        <div className="pt-1 border-t border-slate-200 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                          Do NOT delete the Die-Cut-Line layer — it&apos;s what we cut against.
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="pt-1" data-testid="popular-sizes-panel">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">
                    Popular standard sizes — one click
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {POPULAR_DIE_SIZES.map((sz) => (
                      <div key={sz.label} className="border border-slate-200 rounded-sm p-2 bg-white">
                        <div className="text-[11px] font-bold text-slate-700 mb-1 text-center">{sz.label}</div>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await generateDieTemplate({
                                  shape: "Rectangle",
                                  corners: "Rounded",
                                  heightIn: sz.h,
                                  widthIn: sz.w,
                                  productName: `${product.name} ${sz.label}`,
                                  materialName: currentMaterial?.name || "",
                                  color: config.color,
                                  sku: `${product.sku_prefix}-${sz.w}x${sz.h}`,
                                });
                                logTemplateDownload({
                                  product_id: product.product_id,
                                  product_slug: product.slug,
                                  product_name: product.name,
                                  sku: `${product.sku_prefix}-${sz.w}x${sz.h}`,
                                  width_in: sz.w,
                                  height_in: sz.h,
                                  fmt: "pdf",
                                  shape: "Rectangle",
                                  corners: "Rounded",
                                  material_name: currentMaterial?.name || "",
                                  color: config.color,
                                  source: "popular_size",
                                });
                                toast.success(`${sz.label} PDF downloaded`);
                              } catch (e) {
                                console.error(e);
                                toast.error("Could not generate template");
                              }
                            }}
                            className="flex-1 text-[10px] font-mono uppercase tracking-wider px-1 py-1 border border-slate-300 hover:border-[#333F48] hover:bg-slate-50 transition-colors"
                            data-testid={`popular-pdf-${sz.w}x${sz.h}`}
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              try {
                                generateDieSvg({
                                  shape: "Rectangle",
                                  corners: "Rounded",
                                  heightIn: sz.h,
                                  widthIn: sz.w,
                                  productName: `${product.name} ${sz.label}`,
                                  materialName: currentMaterial?.name || "",
                                  color: config.color,
                                  sku: `${product.sku_prefix}-${sz.w}x${sz.h}`,
                                });
                                logTemplateDownload({
                                  product_id: product.product_id,
                                  product_slug: product.slug,
                                  product_name: product.name,
                                  sku: `${product.sku_prefix}-${sz.w}x${sz.h}`,
                                  width_in: sz.w,
                                  height_in: sz.h,
                                  fmt: "svg",
                                  shape: "Rectangle",
                                  corners: "Rounded",
                                  material_name: currentMaterial?.name || "",
                                  color: config.color,
                                  source: "popular_size",
                                });
                                toast.success(`${sz.label} SVG downloaded`);
                              } catch (e) {
                                console.error(e);
                                toast.error("Could not generate SVG");
                              }
                            }}
                            className="flex-1 text-[10px] font-mono uppercase tracking-wider px-1 py-1 border border-slate-300 hover:border-[#333F48] hover:bg-slate-50 transition-colors"
                            data-testid={`popular-svg-${sz.w}x${sz.h}`}
                          >
                            SVG
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
    </>
  );

  const priceBlock = (
            <>
            <InFlowPriceCard
              ref={inFlowPriceRef}
              price={price}
              calcing={calcing}
              quantity={config.quantity}
            />
            {price?.ladder && (
              <LabelPriceLadder compact price={price} quantity={config.quantity} spec={labelQuoteSpec} onPick={(q) => setConfig((c) => ({ ...c, quantity: q }))} />
            )}
            </>
  );

  // Shipping estimate — reads the product's admin-configured `shipping_tiers`,
  // picks the tier whose min_qty ≤ current qty; preset boxes resolve live from
  // `/api/shipping-presets`, custom rows read inline amounts, else site defaults.
  const shippingBlock = (() => {
              const qty = Number(config.quantity) || 0;
              const tiers = (product.shipping_tiers || [])
                .filter((t) => Number(t.min_qty) > 0)
                .sort((a, b) => Number(a.min_qty) - Number(b.min_qty));
              // Pick the LAST tier whose min_qty is <= qty (largest match).
              let match = null;
              for (const t of tiers) if (Number(t.min_qty) <= qty) match = t;
              const DEFAULTS = { ground: 14.99, three_day: 24.99, two_day: 39.99, next_day: 79.99 };
              let costs = { ...DEFAULTS };
              let sourceLabel = null;
              if (match) {
                const box = match.box_size || "custom";
                if (["XS","S","M","L","XL"].includes(box)) {
                  const presetRow = shippingPresets?.boxes?.[box] || {};
                  costs = {
                    ground:    Number(presetRow.ground)    || 0,
                    three_day: Number(presetRow.three_day) || 0,
                    two_day:   Number(presetRow.two_day)   || 0,
                    next_day:  Number(presetRow.next_day)  || 0,
                  };
                  sourceLabel = `box ${box}`;
                } else {
                  // Custom row — prefer new keys, fall back to legacy express/overnight.
                  costs = {
                    ground:    Number(match.ground)    || 0,
                    three_day: Number(match.three_day) || 0,
                    two_day:   Number(match.two_day ?? match.express)     || 0,
                    next_day:  Number(match.next_day ?? match.overnight)  || 0,
                  };
                  sourceLabel = "custom rate";
                }
              }
              return (
                <div
                  className="mt-3 border border-slate-200 bg-white rounded-sm p-3"
                  data-testid="shipping-estimate-inline"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">
                      Shipping estimate · {qty.toLocaleString()} units
                    </div>
                    {!match ? (
                      <span className="text-[9px] font-mono uppercase text-slate-400" title="Falling back to site-wide default rates" data-testid="ship-source-label">
                        default rates
                      </span>
                    ) : (
                      <span className="text-[9px] font-mono uppercase text-slate-400" data-testid="ship-source-label">
                        {sourceLabel}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="border border-slate-200 rounded-sm py-2">
                      <div className="text-[9px] font-mono tracking-[0.18em] uppercase text-slate-500">Ground</div>
                      <div className="font-display font-black text-sm text-kolt-slate-dark" data-testid="ship-ground">${costs.ground.toFixed(2)}</div>
                    </div>
                    <div className="border border-slate-200 rounded-sm py-2">
                      <div className="text-[9px] font-mono tracking-[0.18em] uppercase text-slate-500">3-Day</div>
                      <div className="font-display font-black text-sm text-kolt-slate-dark" data-testid="ship-three-day">${costs.three_day.toFixed(2)}</div>
                    </div>
                    <div className="border border-slate-200 rounded-sm py-2">
                      <div className="text-[9px] font-mono tracking-[0.18em] uppercase text-slate-500">2-Day</div>
                      <div className="font-display font-black text-sm text-kolt-slate-dark" data-testid="ship-two-day">${costs.two_day.toFixed(2)}</div>
                    </div>
                    <div className="border border-slate-200 rounded-sm py-2">
                      <div className="text-[9px] font-mono tracking-[0.18em] uppercase text-slate-500">Next Day</div>
                      <div className="font-display font-black text-sm text-kolt-slate-dark" data-testid="ship-next-day">${costs.next_day.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 mt-2 text-center">
                    Ships nationwide from Houston, TX · Same-day cut-off 11 AM CT
                  </div>
                </div>
              );
  })();

  const hasAddonsOrTurnaround = product.addons?.length > 0 || (product.turnaround_options || []).length > 0;
  const addonsBlock = (
    <>
            {product.addons?.length > 0 && (
              <div className="mt-5">
                <ProductAddons
                  addons={product.addons}
                  selectedKeys={selectedAddons}
                  quantity={config.quantity}
                  onToggle={(k) =>
                    setSelectedAddons((arr) =>
                      arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]
                    )
                  }
                />
              </div>
            )}

            {/* Turnaround picker — only renders when the admin has configured options */}
            <TurnaroundPicker
              options={product.turnaround_options || []}
              selectedKey={turnaroundKey}
              onChange={setTurnaroundKey}
            />
    </>
  );

  const artworkBlock = (
    <>
            {LARGE_FORMAT_CATEGORIES.has(product.service_category) ? (
              <div className="mt-5">
                <LargeFormatJobs
                  user={user}
                  product={product}
                  configHeightIn={config.height_in}
                  configWidthIn={config.width_in}
                  configShape={config.shape}
                  onJobsChange={setJobs}
                />
              </div>
            ) : (
              <ArtworkUploader
                user={user}
                productSlug={product.slug}
                productCustomDimensions={product.custom_dimensions}
                configHeightIn={config.height_in}
                configWidthIn={config.width_in}
                configShape={config.shape}
                uploadedFile={uploadedFile}
                onUploaded={setUploadedFile}
                onCleared={() => setUploadedFile(null)}
              />
            )}
            <ProductTools product={product} configWidthIn={config.width_in} configHeightIn={config.height_in} onApplySize={(product.custom_dimensions || product.size_input_mode === "sqft") ? (w, h) => applySizeFromTool(w, h) : null} />
    </>
  );

  const notesBlock = (
            <div className="mt-5" data-testid="order-data-section">
              <div className="overline mb-2">Order data / production notes</div>
              <textarea
                value={productionNotes}
                onChange={(e) => setProductionNotes(e.target.value)}
                rows={3}
                placeholder="Special instructions for the designer — bleed area, variable data CSV details, proof preferences..."
                className="input-base resize-y"
                data-testid="production-notes"
              />
              <div className="text-xs text-slate-500 mt-1">
                Our designer will reach out with a proof PDF within 24 hrs for your approval.
              </div>
            </div>
  );

  const actionsBlock = (
    <>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <Button onClick={addToCart} className="btn-primary" data-testid="add-to-cart-btn" disabled={!price}>
                <ShoppingCart className="w-4 h-4" /> Add to Cart
              </Button>
              <Button onClick={saveQuote} className="btn-outline" disabled={saving} data-testid="save-quote-btn">
                <Bookmark className="w-4 h-4" /> {saving ? "Saving..." : "Save Quote"}
              </Button>
            </div>

            <div className="mt-4 text-xs text-slate-500 flex items-center justify-center gap-2">
              <Truck className="w-3.5 h-3.5" />
              100% reprint guarantee
            </div>

            <Link to="/quote" className="mt-4 flex items-center justify-center gap-2 text-sm font-bold text-[#333F48] hover:text-[#DD6B33] transition-colors" data-testid="custom-quote-link">
              <Sparkles className="w-4 h-4" /> Need a custom size or volume? Request quote
            </Link>
    </>
  );

  if (isMobile) {
    return (
      <MobileProductLayout
        product={product}
        gallery={gallery}
        activeImage={activeImage}
        setActiveImage={setActiveImage}
        price={price}
        calcing={calcing}
        quantity={config.quantity}
        onAddToCart={addToCart}
        hasAddonsOrTurnaround={hasAddonsOrTurnaround}
        isWedding={isWedding}
        blocks={{ breadcrumbs, specBlock, priceBlock, shippingBlock, addonsBlock, artworkBlock, notesBlock, actionsBlock, seoBody, tabsCard, stockGuide: product.label_model ? <LabelStockGuide currentStock={product.label_model.stock} spec={{ ...labelQuoteSpec, quantity: config.quantity }} /> : null }}
      />
    );
  }

  return (
    <main className={`${pageBgClass} min-h-screen pb-20`} data-testid="product-detail-page">
      {/* Floating price overlay — visible until user scrolls to the in-flow price box */}
      <FloatingPriceCard
        price={price}
        calcing={calcing}
        quantity={config.quantity}
        isInView={isPriceInView}
        visible={hasCustomDims}
      />

      {breadcrumbs}

      <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT: Gallery + Specs */}
        <div className="lg:col-span-7 space-y-6">
          {galleryCard}
          {product.label_model && <LabelStockGuide currentStock={product.label_model.stock} spec={{ ...labelQuoteSpec, quantity: config.quantity }} />}
          {seoBody}
          {tabsCard}
        </div>

        {/* RIGHT: Configurator */}
        <div className="lg:col-span-5">
          <div className={`${cardClass} p-6 sticky top-32`} id="configurator" data-testid="configurator">
            <div className="overline mb-3">Specification</div>
            {specBlock}
            {priceBlock}
            {shippingBlock}
            {addonsBlock}
            {artworkBlock}
            {notesBlock}
            {actionsBlock}
          </div>
        </div>
      </div>
      {ownerEditing && product && (
        <Suspense fallback={null}>
          <ProductEditDialog product={product} onClose={() => setOwnerEditing(false)} onSaved={() => { setOwnerEditing(false); setReloadKey((k) => k + 1); }} />
        </Suspense>
      )}
    </main>
  );
}
